import React, { useState, useEffect } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Search, Plus, Trash2, Eye, ExternalLink, Loader2, X } from 'lucide-react';

const ProjectList = ({ onNewProject, onViewProject }) => {
  const { fetchProjects, deleteProject, projects, totalProjects, loading } = useProjects();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [tags, setTags] = useState([]);
  const { getTags, getSettings } = useProjects();
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    const loadMeta = async () => {
      const tagsData = await getTags();
      setTags(tagsData || []);
      const settingsData = await getSettings();
      setPlatforms(settingsData?.platforms || []);
    };
    loadMeta();
  }, []);

  useEffect(() => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (platformFilter !== 'all') filters.platform = platformFilter;
    if (tagFilter !== 'all') filters.tag = tagFilter;
    
    const timer = setTimeout(() => { fetchProjects(filters); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, platformFilter, tagFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await deleteProject(id);
    fetchProjects();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPlatformFilter('all');
    setTagFilter('all');
  };

  const statuses = ['Prospective', 'Applied', 'Client Responded', 'Offered', 'On Hold', 'Contract Started', 'Contract Ended', 'Rejected'];
  const hasFilters = searchQuery || statusFilter !== 'all' || platformFilter !== 'all' || tagFilter !== 'all';

  const getStatusBadge = (status) => {
    const colors = {
      'Prospective': 'bg-slate-100 text-slate-700',
      'Applied': 'bg-blue-100 text-blue-700',
      'Client Responded': 'bg-cyan-100 text-cyan-700',
      'Offered': 'bg-emerald-100 text-emerald-700',
      'On Hold': 'bg-amber-100 text-amber-700',
      'Contract Started': 'bg-green-100 text-green-700',
      'Contract Ended': 'bg-purple-100 text-purple-700',
      'Rejected': 'bg-red-100 text-red-700'
    };
    return <Badge className={`${colors[status] || 'bg-gray-100 text-gray-700'} border-0`}>{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects..."
                  className="pl-9"
                  data-testid="project-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="project-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[140px]" data-testid="project-platform-filter">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-[140px]" data-testid="project-tag-filter">
                <SelectValue placeholder="Tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {tags.map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                <X className="w-3 h-3" />Clear
              </Button>
            )}
            <Button onClick={onNewProject} className="bg-blue-600 hover:bg-blue-700" data-testid="new-project-list-btn">
              <Plus className="w-4 h-4 mr-1" />New Project
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                  </TableCell>
                </TableRow>
              ) : projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No projects found
                  </TableCell>
                </TableRow>
              ) : projects.map(project => (
                <TableRow key={project.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onViewProject(project.id)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{project.title}</span>
                      {project.project_url && (
                        <a href={project.project_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          <ExternalLink className="w-3.5 h-3.5 text-blue-500" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{project.platform}</Badge></TableCell>
                  <TableCell>{getStatusBadge(project.status)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{project.client_name || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[150px]">
                      {project.tags?.slice(0, 2).map((t, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600">{t}</Badge>
                      ))}
                      {project.tags?.length > 2 && <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{project.tags.length - 2}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{project.proposal_date}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onViewProject(project.id); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="p-3 text-sm text-slate-500 border-t">{totalProjects} project{totalProjects !== 1 ? 's' : ''}</div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectList;
